/* Beat-Sonic USA: Force vehicle selection + JSON-LD offerCount safeguard.
   ---------------------------------------------------------------------------
   VERSION 3.2 - Aug 12 2026. Replaces the Aug 4 2026 version in full.

   Every vehicle-specific product has a real option value "PLEASE SELECT YOUR
   VEHICLE" on its vehicle option. On load we move that placeholder to the TOP
   of the dropdown and select it so nothing is pre-configured, and we DRIVE the
   hidden variant select (select[name="id"]) ourselves - matching the currently
   chosen option value(s) to the right variant - because this theme does not
   reliably sync the visible selectors to the variant id. Works for
   single-option (amps, ShiftPower, mirroring, cameras, nav bypass) and
   multi-option (S-Connect + harness, cs6ep switch, ena-2s1/3s1 audio-system)
   products. While the vehicle placeholder is selected, Add-to-Cart and
   Buy-it-Now / dynamic checkout are disabled (greyed) with an inline red
   message. Front-end only.

   WHY V3 EXISTS
   Order #1000471227 (ENA-3T3, Aug 12 2026) completed with the placeholder
   variant despite this guard being live and correct. 58 such orders in the
   trailing 12 months, $47,585. The placeholder is a REAL, purchasable variant
   with inventory tracking off, so Shopify itself will never refuse it - this
   script is the only thing standing between a blank order and checkout. V2 had
   four ways to lose that fight:

     1. It only set button.disabled. A disabled button still allows the form to
        be submitted by pressing Enter in the quantity field, and it does not
        stop a theme script that binds its own click handler.
     2. It re-checked state on 'change' and once at 300ms. If Turbo's own
        variant handler re-enables the button or re-points select[name="id"]
        after that last pass, the guard is simply gone. This is the most likely
        cause of #1000471227.
     3. It never saw forms injected after DOMContentLoaded (quick-view modal,
        sticky add-to-cart bar).
     4. The data-bs-soldout snapshot was taken AFTER the placeholder was
        selected. If the theme marks the button disabled at that moment, the
        script records "sold out" permanently and Add to Cart never re-enables,
        even once a real vehicle is picked.

   V3 CHANGES
     - Sold-out state is snapshotted on the FORM, once, BEFORE the placeholder
       is selected, and is derived from the server-rendered markup (the theme's
       true sold-out branch in snippets/product.buttons.liquid renders a button
       with data-atc and NO name="add").
     - Capture-phase click blocking on the cart button, so theme AJAX handlers
       never fire.
     - Capture-phase submit blocking on the form, so the Enter key and any
       programmatic submit are stopped.
     - MutationObserver re-asserts the lock if anything re-enables the button.
       Replaces the 300ms guess.
     - MutationObserver on document.body picks up forms injected later.
     - Last line of defence: any /cart/add request (fetch or XHR) carrying a
       known placeholder variant ID is refused client-side. This covers every
       add-to-cart path on the site at once, including ones this script has
       never seen.

   V3.1 CHANGES (the ENA-2S1 report: product sometimes loads with a vehicle
   already picked instead of the placeholder)
     - V3 re-asserted the LOCK but never the SELECTION. Anything that moved the
       dropdown off the placeholder without the customer touching it left the
       guard seeing a legitimately chosen vehicle, and it unlocked. Three silent
       causes: browser form-state restoration on reload and back-navigation
       (Firefox restores AFTER DOMContentLoaded, landing on top of us), bfcache
       restore (initForm never runs again because data-bs-init is still 1),
       and the theme's linked-option logic snapping option 1 to the first
       available value. ENA-2S1 is the shape most likely to hit the third:
       15 vehicles x 4 audio systems with only some combinations real.
     - autocomplete=off on the form and every select, which kills the browser
       restoration path outright.
     - enforcePlaceholder(): until the customer actually picks a vehicle in this
       pageview, the placeholder is put back every time. Once they pick, it
       never fights them again.
     - User intent is detected from a trusted change event OR any user gesture
       anywhere in the document within 2s. Gesture tracking is DOCUMENT-level on
       purpose: a styled dropdown may render its list in a portal outside the
       form, and wrongly overriding a real choice (the customer's vehicle
       visibly snapping back) is far worse than accepting a browser restore.
     - An explicit ?variant= deep link to a REAL variant counts as a choice, so
       Shopping-feed and guide-page links still work.
     - Safety valve: after 3 enforcements on one form we stop. If something keeps
       fighting us, a dropdown that snaps back under the customer's hand is worse
       than the problem. The lock and the network guard still prevent a blank
       order.

   V3.2 CHANGES (Eric: "still defaults to 2022-2026 Toyota GR86" on ENA-2S1)
     - Diagnosed on the LIVE page, not by inference. The guard was working
       correctly: dropdown on the placeholder, select[name="id"] at -1, Add to
       Cart disabled with class bs-atc-locked, warning message present. Nothing
       was preselected and nothing could be added to cart.
     - What Eric saw was the option LABEL, which Turbo renders as
       "PLEASE SELECT YOUR VEHICLE BELOW: <span>2022 - 2026 TOYOTA GR86</span>".
       That span is written server-side from selected_or_first_available_variant
       and Turbo only refreshes it from its own variant handler, which does not
       run when no variant matches. A DOM sweep confirmed it was the ONLY
       visible element on the page showing the stale vehicle name.
     - syncOptionLabels() now keeps every option label in step with its select.
       Cosmetic, but it is the line the customer actually reads.

   LIVE BUG FOUND AND FIXED WHILE VERIFYING V3.2, predates all of this work
     - On ena-2s1, picking a real vehicle while the second option still held an
       incompatible value left select[name="id"] at -1 (no such variant) while
       Add to Cart was ENABLED. The add posts no variant id and fails with
       "Required parameter missing or invalid: items" - to the customer, Add to
       Cart simply does nothing. Affects every multi-option product: ena-2s1,
       ena-3s1, the S-Connect plus harness kits, cs6ep.
     - isLocked() is now lockReason(), which also locks when no variant
       resolves, with its own message.

   RESIDUAL RISK, STATED PLAINLY: this is still client-side. If the file fails
   to load at all, the placeholder is not selected either, so the default falls
   back to the first REAL vehicle - a wrong-fitment order rather than a blank
   one. The only bypass-proof fix is a Shopify Cart and Checkout Validation
   Function. This script narrows the hole; it does not close it.
*/
(function () {
  'use strict';

  var PLACEHOLDER = 'PLEASE SELECT YOUR VEHICLE';
  var GUARD_VERSION = '3.2.0';

  function norm(s) { return (s || '').replace(/\s+/g, ' ').trim().toUpperCase(); }
  var PH = norm(PLACEHOLDER);
  function isPh(s) { return norm(s).indexOf(PH) !== -1; }
  function isVehicleName(s) { return /please\s+select\s+your\s+vehicle/i.test(s || ''); }

  // Aug 2 2026 fix, retained. Master <select name="id"> options are rendered as
  // "{{ variant.title }} - {{ price }}" (snippets/product.variants.liquid) and
  // vehicle titles routinely start with a spaced year range ("2022 - 2026 ...")
  // which itself contains " - ". Strip the price using the LAST " - " only.
  function normText(t) { return (t || '').replace(/\s+/g, ' ').trim(); }
  function stripPrice(t) {
    t = normText(t);
    var i = t.lastIndexOf(' - ');
    return i === -1 ? t : t.slice(0, i);
  }

  /* ---------------------------------------------------------------------
     Placeholder variant ID registry.
     Every placeholder variant ID we can see anywhere on the page is recorded
     here. The network guard at the bottom refuses any /cart/add carrying one.
     --------------------------------------------------------------------- */
  var PH_IDS = Object.create(null);
  var PH_ID_COUNT = 0;

  function registerPhIds(root) {
    var masters = (root || document).querySelectorAll('form[action*="/cart/add"] select[name="id"], form[action*="/cart/add"] input[name="id"]');
    for (var i = 0; i < masters.length; i++) {
      var el = masters[i];
      if (el.tagName === 'SELECT') {
        for (var j = 0; j < el.options.length; j++) {
          var o = el.options[j];
          if (isPh(stripPrice(o.textContent)) && o.value) {
            if (!PH_IDS[String(o.value)]) { PH_IDS[String(o.value)] = true; PH_ID_COUNT++; }
          }
        }
      }
    }
  }

  function isPhId(id) {
    return !!(id && PH_IDS[String(id).trim()]);
  }

  /* ---------------------------------------------------------------------
     Region gate awareness (Aug 4 2026, retained).
     snippets/product.buttons.liquid swaps Add to Cart for an "Inquire Now"
     ANCHOR on Japan-sourced products viewed from outside US/CA/MX. When that
     happens there is no cart button to guard and nothing to lock.
     --------------------------------------------------------------------- */
  function isRegionBlocked(form) {
    return !!form.querySelector('[data-bs-region-restricted="true"][data-bs-blocked="true"]');
  }

  function clearMsgs(form) {
    var msgs = form.querySelectorAll('.bs-select-msg');
    for (var i = 0; i < msgs.length; i++) {
      if (msgs[i].parentNode) msgs[i].parentNode.removeChild(msgs[i]);
    }
  }

  function cartButton(form) {
    return form.querySelector('button[name="add"], button[data-atc], input[type="submit"][name="add"]');
  }

  function optionNameFor(sel) {
    var lbl = null;
    if (sel.id) { try { lbl = document.querySelector('label[for="' + sel.id + '"]'); } catch (e) {} }
    if (!lbl && sel.closest) { var w = sel.closest('.selector-wrapper'); if (w) lbl = w.querySelector('label, legend'); }
    var t = lbl ? lbl.textContent : '';
    return t.split(':')[0];
  }

  function vehicleSelector(form) {
    var sels = form.querySelectorAll('.single-option-selector');
    for (var i = 0; i < sels.length; i++) {
      if (isVehicleName(optionNameFor(sels[i]))) return sels[i];
    }
    for (var m = 0; m < sels.length; m++) {
      for (var j = 0; j < sels[m].options.length; j++) {
        if (isPh(sels[m].options[j].value)) return sels[m];
      }
    }
    return null;
  }

  function phOptionIn(sel) {
    if (!sel) return null;
    for (var j = 0; j < sel.options.length; j++) { if (isPh(sel.options[j].value)) return sel.options[j]; }
    return null;
  }

  function injectPlaceholder(sel) {
    if (!sel || phOptionIn(sel)) return;
    var o = document.createElement('option');
    o.value = PLACEHOLDER;
    o.textContent = 'Please select your vehicle';
    o.setAttribute('data-bs-ph', '1');
    o.selected = true;
    sel.insertBefore(o, sel.firstChild);
    sel.selectedIndex = 0;
  }

  function syncMaster(form) {
    var vs = vehicleSelector(form);
    var master = form.querySelector('select[name="id"]');
    if (!vs || !master) return;
    var vopt = vs.options[vs.selectedIndex];
    if (vopt && isPh(vopt.value)) {
      if (master.selectedIndex !== -1) master.selectedIndex = -1;
      return;
    }
    var sels = form.querySelectorAll('.single-option-selector');
    var vals = [];
    for (var i = 0; i < sels.length; i++) {
      var o = sels[i].options[sels[i].selectedIndex];
      if (o) vals.push(normText(o.textContent));
    }
    var found = -1;
    for (var k = 0; k < master.options.length; k++) {
      var mt = stripPrice(master.options[k].textContent);
      var ok = true;
      for (var v = 0; v < vals.length; v++) { if (mt.indexOf(vals[v]) === -1) { ok = false; break; } }
      if (ok) { found = k; break; }
    }
    if (found !== -1) {
      if (master.selectedIndex !== found) {
        master.selectedIndex = found;
        try { master.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
      }
    } else if (master.selectedIndex !== -1) {
      master.selectedIndex = -1;
    }
  }

  /* ---------------------------------------------------------------------
     lockReason() - why the form is locked, or null if it is not.

     'vehicle'   the vehicle dropdown is still on the placeholder
     'novariant' a vehicle IS chosen but the option combination resolves to no
                 real variant

     The 'novariant' case is a LIVE BUG found on ena-2s1 on Aug 12 2026 while
     verifying V3.2 in a real browser, and it predates all of this work - V2's
     isLocked() had the same hole. Picking "2022 - 2026 SUBARU WRX" while the
     Audio System option was still on "GR86/BRZ" left select[name="id"] at -1,
     because no such variant exists, yet Add to Cart was ENABLED. Clicking it
     posts no variant id, so /cart/add fails with "Required parameter missing or
     invalid: items" - the customer just sees Add to Cart do nothing.

     Affects every multi-option product: ena-2s1, ena-3s1, the S-Connect plus
     harness kits, and cs6ep.

     This does not cost a sale that would otherwise have completed: with the
     master select at -1 the add was already guaranteed to fail. It converts a
     silent failure into a message. The risk to watch is the inverse - if
     syncMaster() ever fails to match a combination that IS real, this now
     greys the button instead of adding the wrong thing, so a matching bug
     becomes visible rather than silent. That is the safer direction, but it
     does mean a matching regression would block sales, so multi-option
     products need a spot-check after any change to stripPrice/syncMaster.
     --------------------------------------------------------------------- */
  function lockReason(form) {
    var vs = vehicleSelector(form);
    if (!vs) return null;
    var sel = vs.options[vs.selectedIndex];
    if (sel && isPh(sel.value)) return 'vehicle';
    var master = form.querySelector('select[name="id"]');
    if (master) {
      if (master.selectedIndex === -1) return 'novariant';
      var mo = master.options[master.selectedIndex];
      // Belt and braces: if the master somehow points AT a known placeholder
      // variant id, lock regardless of what the visible dropdown claims.
      if (mo && isPhId(mo.value)) return 'vehicle';
    }
    return null;
  }

  function isLocked(form) { return lockReason(form) !== null; }

  /* ---------------------------------------------------------------------
     Sold-out snapshot.
     V2 BUG: this was read from the button AFTER the placeholder had been
     selected, so a theme that disables the button on an unavailable variant
     could poison the flag permanently. V3 reads it once, on the FORM, from the
     server-rendered markup, before anything is touched. The theme's genuine
     sold-out branch renders <button ... disabled data-atc> with NO name="add".
     --------------------------------------------------------------------- */
  function snapshotSoldOut(form) {
    if (form.__bsSoldOut !== undefined) return;
    var atc = cartButton(form);
    if (!atc) { form.__bsSoldOut = false; return; }
    var hasAddName = atc.getAttribute('name') === 'add';
    form.__bsSoldOut = !hasAddName && atc.disabled === true;
  }

  /* ---------------------------------------------------------------------
     apply(): the lock itself.

     EVERY WRITE IN HERE IS CONDITIONAL, AND THAT IS LOAD-BEARING.
     apply() is driven by a MutationObserver. classList.toggle() and
     setAttribute() emit a mutation record even when the value does not change,
     so an unconditional write makes apply() retrigger itself forever and pegs
     the CPU. A stable state must produce ZERO mutations. Do not "simplify"
     these guards away.
     --------------------------------------------------------------------- */
  function setClass(el, name, want) {
    if (!el) return;
    var has = el.classList.contains(name);
    if (has === want) return;
    if (want) el.classList.add(name); else el.classList.remove(name);
  }

  function setAttr(el, name, value) {
    if (!el) return;
    if (value === null) {
      if (el.hasAttribute(name)) el.removeAttribute(name);
      return;
    }
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  /* ---------------------------------------------------------------------
     Placeholder ENFORCEMENT (V3.1, Aug 12 2026).

     V3 re-asserted the LOCK but never re-asserted the SELECTION. If anything
     moved the visible dropdown off the placeholder without the customer
     touching it, the guard saw a legitimately chosen vehicle and unlocked. Eric
     hit this on ENA-2S1: the product sometimes loads with a vehicle already
     picked. Three things can do that, and all three are silent:

       - Browser form-state restoration. Chrome and Firefox restore a <select>
         by INDEX on reload and on back-navigation, and Firefox does it after
         DOMContentLoaded, so it lands on top of our selection.
       - bfcache restore. initForm() never runs again (data-bs-init is still
         "1"), so the restored selection stood.
       - The theme's own linked-option logic snapping option 1 to the first
         available value when a combination changes. ENA-2S1 is the shape most
         likely to trigger this: two options, 15 vehicles x 4 audio systems,
         with only some combinations real.

     Rule: until the customer actually picks a vehicle in this pageview, the
     placeholder is the only allowed selection and we put it back every time.
     Once they have picked, we never fight them again.
     --------------------------------------------------------------------- */
  function markUserPicked(form) { form.__bsUserPicked = true; }

  function userHasPicked(form) { return form.__bsUserPicked === true; }

  // Gesture tracking is DOCUMENT-level, deliberately.
  //
  // Turbo may render a styled dropdown that overlays the native select and sets
  // its value from script. Such a widget often renders its option list in a
  // portal OUTSIDE the <form>, so a form-scoped listener would see neither a
  // trusted change nor a gesture, and we would fight a customer's real choice -
  // their vehicle would visibly snap back to the placeholder. That is a far
  // worse failure than accepting a browser form-restore as intentional, so the
  // gesture window is global and generous.
  var lastGestureAt = 0;
  function trackGlobalGestures() {
    if (trackGlobalGestures.done) return;
    trackGlobalGestures.done = true;
    ['pointerdown', 'mousedown', 'keydown', 'touchstart'].forEach(function (evt) {
      document.addEventListener(evt, function () { lastGestureAt = +new Date(); }, true);
    });
  }

  function trackUserIntent(form) {
    form.addEventListener('change', function (e) {
      // A real interaction is either a trusted event or a change that follows a
      // real user gesture anywhere on the page. A theme script setting
      // selectedIndex at load, or the browser restoring form state, is neither.
      if (e.isTrusted === true || (+new Date() - lastGestureAt) < 2000) {
        var vs = vehicleSelector(form);
        var opt = vs && vs.options[vs.selectedIndex];
        if (opt && !isPh(opt.value)) markUserPicked(form);
      }
    }, true);
  }

  function enforcePlaceholder(form) {
    if (userHasPicked(form)) return false;
    // SAFETY VALVE. If we have already put the placeholder back three times and
    // something keeps moving it, we are in a fight we cannot win, and a dropdown
    // that visibly snaps back while a customer is using it is worse than the
    // problem we are solving. Back off and let the lock plus the network guard
    // do the work - a blank order still cannot be placed.
    if ((form.__bsEnforced || 0) >= 3) return false;
    var vs = vehicleSelector(form);
    if (!vs) return false;
    var cur = vs.options[vs.selectedIndex];
    if (cur && isPh(cur.value)) return false;   // already correct, no write
    var ph = phOptionIn(vs);
    if (!ph) return false;
    form.__bsEnforced = (form.__bsEnforced || 0) + 1;
    if (vs.firstChild !== ph) vs.insertBefore(ph, vs.firstChild);
    ph.selected = true;
    try { vs.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
    return true;
  }

  /* ---------------------------------------------------------------------
     Option-label sync (V3.2, Aug 12 2026).

     Turbo renders each option's label as:
         <label for="...">PLEASE SELECT YOUR VEHICLE BELOW: <span>value</span></label>
     and that <span> is written SERVER-SIDE from
     product.selected_or_first_available_variant. Turbo only refreshes it from
     its own variant handler, and with the placeholder selected there is no
     matching variant, so it leaves the server-rendered value in place.

     Result on ENA-2S1: the dropdown correctly read "PLEASE SELECT YOUR
     VEHICLE", the master select was -1, and Add to Cart was locked - but the
     heading right above the dropdown still read
         "PLEASE SELECT YOUR VEHICLE BELOW: 2022 - 2026 TOYOTA GR86"
     which reads to a customer exactly like a preselected vehicle. Confirmed on
     the live page: that <span> was the ONLY visible element on the whole page
     showing the stale name. Cosmetic, but it is the thing the customer reads,
     and it undermines the entire point of the placeholder.

     Writes are conditional, same as everything else in apply().
     --------------------------------------------------------------------- */
  function labelFor(sel) {
    var lbl = null;
    if (sel.id) { try { lbl = document.querySelector('label[for="' + sel.id + '"]'); } catch (e) {} }
    if (!lbl && sel.closest) { var w = sel.closest('.selector-wrapper'); if (w) lbl = w.querySelector('label'); }
    return lbl;
  }

  function syncOptionLabels(form) {
    var sels = form.querySelectorAll('.single-option-selector');
    for (var i = 0; i < sels.length; i++) {
      var sel = sels[i];
      var opt = sel.options[sel.selectedIndex];
      if (!opt) continue;
      var lbl = labelFor(sel);
      if (!lbl) continue;
      var spans = lbl.querySelectorAll('span');
      if (!spans.length) continue;
      // Prefer the span currently holding one of THIS select's option values -
      // that is unambiguously the current-value span. Fall back to the last
      // span, which is where Turbo puts it.
      var target = null;
      for (var s = 0; s < spans.length && !target; s++) {
        var txt = normText(spans[s].textContent);
        if (!txt) continue;
        for (var j = 0; j < sel.options.length; j++) {
          if (normText(sel.options[j].textContent) === txt) { target = spans[s]; break; }
        }
      }
      if (!target) target = spans[spans.length - 1];
      var want = normText(opt.textContent);
      if (normText(target.textContent) !== want) target.textContent = want;
    }
  }

  function apply(form) {
    if (isRegionBlocked(form)) {
      clearMsgs(form);
      form.querySelectorAll('.shopify-payment-button, .dynamic-checkout__content, [data-shopify="payment-button"]').forEach(function (el) {
        setClass(el, 'bs-vehicle-lock', false);
      });
      form.__bsLocked = false;
      return;
    }

    enforcePlaceholder(form);
    syncMaster(form);
    syncOptionLabels(form);
    var reason = lockReason(form);
    var locked = reason !== null;
    form.__bsLocked = locked;

    var atc = cartButton(form);
    if (atc) {
      var soldOut = form.__bsSoldOut === true;
      var want = locked ? true : soldOut;
      if (atc.disabled !== want) atc.disabled = want;
      setClass(atc, 'bs-atc-locked', locked && !soldOut);
      setAttr(atc, 'aria-disabled', locked ? 'true' : null);
    }

    form.querySelectorAll('.shopify-payment-button, .dynamic-checkout__content, [data-shopify="payment-button"]').forEach(function (el) {
      setClass(el, 'bs-vehicle-lock', locked);
    });

    var section = form.querySelector('.purchase-section') || form;
    // Search the whole FORM, not just the section: when atc is null the message
    // is inserted as a sibling OF the section, which section.querySelector can
    // never see. That mismatch produced duplicate, unremovable warnings in V1.
    var wantText = reason === 'novariant'
      ? 'That combination is not available. Please check your selections.'
      : 'Select your vehicle to continue.';
    var msg = form.querySelector('.bs-select-msg');
    if (locked) {
      if (msg && normText(msg.textContent) !== wantText) msg.textContent = wantText;
      if (!msg) {
        msg = document.createElement('p');
        msg.className = 'bs-select-msg';
        msg.setAttribute('role', 'alert');
        msg.textContent = wantText;
        var anchor = atc ? ((atc.closest && atc.closest('.purchase')) || atc.parentNode) : section;
        (anchor.parentNode || section).insertBefore(msg, anchor.nextSibling);
      }
    } else if (msg) {
      clearMsgs(form);
    }
  }

  /* ---------------------------------------------------------------------
     Hard event blocking.
     Capture phase, so we run BEFORE any theme handler bound on the button or
     the form. stopImmediatePropagation prevents the theme's AJAX add from
     firing at all. This is what a bare button.disabled never did.
     --------------------------------------------------------------------- */
  function blockEvent(e, form) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    apply(form);
    var msg = form.querySelector('.bs-select-msg');
    if (msg && msg.scrollIntoView) {
      try { msg.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) {}
    }
    var vs = vehicleSelector(form);
    if (vs && vs.focus) { try { vs.focus(); } catch (err) {} }
    return false;
  }

  function attachHardGuards(form) {
    // Submit: catches the Enter key in the quantity field and any programmatic
    // form.submit()-adjacent dispatch. A disabled button does not stop this.
    form.addEventListener('submit', function (e) {
      if (isRegionBlocked(form)) return;
      if (isLocked(form)) return blockEvent(e, form);
    }, true);

    // Click: capture phase on the form, so it fires before handlers bound
    // directly to the button by the theme's AJAX cart.
    form.addEventListener('click', function (e) {
      if (isRegionBlocked(form)) return;
      var t = e.target;
      var btn = t && t.closest ? t.closest('button, input[type="submit"], a') : null;
      if (!btn) return;
      var isCart = btn.getAttribute('name') === 'add' ||
                   btn.hasAttribute('data-atc') ||
                   (btn.classList && btn.classList.contains('add-to-cart'));
      if (!isCart) return;
      if (btn.classList && btn.classList.contains('bs-inquire')) return;
      if (isLocked(form)) return blockEvent(e, form);
    }, true);

    // Keyboard: Enter or Space on the cart button.
    form.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.keyCode !== 13 && e.keyCode !== 32) return;
      if (isRegionBlocked(form)) return;
      if (isLocked(form)) {
        var t = e.target;
        if (t && t.tagName === 'SELECT') return; // let the dropdown work
        return blockEvent(e, form);
      }
    }, true);
  }

  /* ---------------------------------------------------------------------
     Re-assertion observer.
     V2 checked state on 'change' and once at 300ms. If the theme's variant
     handler re-enabled the button or re-pointed select[name="id"] after that,
     the guard was gone with no way to notice. This watches for exactly that and
     puts the lock straight back. apply() is idempotent, which is what stops it
     reacting to its own writes and looping.

     KNOWN LIMIT: changing select.selectedIndex from script mutates a PROPERTY,
     not an attribute, so MutationObserver cannot see it. If the theme re-points
     select[name="id"] at the placeholder variant that way, the observer stays
     silent. The click and submit guards still hold, because they call isLocked()
     live at event time rather than trusting cached state, and the network guard
     refuses the request regardless. attachWatchdog() below closes the remaining
     UI-state gap with a cheap 1s check.
     --------------------------------------------------------------------- */
  function attachWatchdog(form) {
    setInterval(function () {
      if (isRegionBlocked(form)) return;
      var master = form.querySelector('select[name="id"]');
      var pointingAtPh = false;
      if (master && master.selectedIndex !== -1) {
        var mo = master.options[master.selectedIndex];
        if (mo && isPhId(mo.value)) pointingAtPh = true;
      }
      // Also catch silent DRIFT: the dropdown moved off the placeholder without
      // the customer picking anything (browser form restore, theme
      // linked-option snap). isLocked() reads false in that state, so a
      // lock-state comparison alone would never notice.
      var drifted = false;
      if (!userHasPicked(form)) {
        var vs = vehicleSelector(form);
        var cur = vs && vs.options[vs.selectedIndex];
        if (cur && !isPh(cur.value)) drifted = true;
      }
      if (pointingAtPh || drifted || isLocked(form) !== form.__bsLocked) apply(form);
    }, 1000);
  }

  function attachObserver(form) {
    if (typeof MutationObserver !== 'function') {
      // No observer available: fall back to a slow poll rather than nothing.
      setInterval(function () { if (!isRegionBlocked(form)) apply(form); }, 750);
      return;
    }
    var pending = false;
    // Circuit breaker. apply() is written to be idempotent, so a stable page
    // settles within one or two passes. If something on the page fights back
    // and we exceed this, stop observing and poll instead - a busy loop in a
    // customer's browser is worse than a slower guard.
    var burst = 0;
    var burstStart = 0;
    var obs = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        var now = +new Date();
        if (now - burstStart > 1000) { burstStart = now; burst = 0; }
        burst++;
        if (burst > 40) {
          obs.disconnect();
          setInterval(function () { if (!isRegionBlocked(form)) apply(form); }, 750);
          return;
        }
        apply(form);
      }, 0);
    });
    obs.observe(form, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'value', 'selected', 'aria-disabled']
    });
    form.__bsObserver = obs;
  }

  /* ---------------------------------------------------------------------
     Per-form init.
     --------------------------------------------------------------------- */
  function initForm(form) {
    if (form.getAttribute('data-bs-init') === '1') return;
    form.setAttribute('data-bs-init', '1');
    form.setAttribute('data-bs-guard', GUARD_VERSION);

    // MUST run before the placeholder is selected. See snapshotSoldOut().
    snapshotSoldOut(form);
    registerPhIds(form);
    trackGlobalGestures();
    trackUserIntent(form);

    // Stop the browser restoring a previously chosen vehicle on reload or
    // back-navigation. This is the cheapest half of the ENA-2S1 fix; the other
    // half is enforcePlaceholder(), for the cases autocomplete cannot cover.
    form.setAttribute('autocomplete', 'off');
    form.querySelectorAll('select, input').forEach(function (el) {
      el.setAttribute('autocomplete', 'off');
    });

    var vs = vehicleSelector(form);
    if (vs) {
      var ph = phOptionIn(vs);
      // Always move the placeholder to the TOP of the dropdown.
      if (ph && vs.firstChild !== ph) vs.insertBefore(ph, vs.firstChild);

      var deepLink = /variant=(\d+)/.exec(location.search);
      if (deepLink && !isPhId(deepLink[1])) {
        // An explicit ?variant= link to a REAL variant is a deliberate choice
        // (a Shopping feed click, a link from a guide page). Honour it, and
        // record it as picked so enforcePlaceholder never fights it.
        markUserPicked(form);
      } else if (ph) {
        ph.selected = true;
        try { vs.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
      } else {
        injectPlaceholder(vs); // fallback if a real placeholder value is missing
      }
    }

    attachHardGuards(form);
    apply(form);
    form.addEventListener('change', function () { setTimeout(function () { apply(form); }, 0); });
    attachObserver(form);
    attachWatchdog(form);

    // The region gate's country fetch resolves asynchronously, so a form can
    // become region-blocked AFTER the passes above have run.
    setTimeout(function () { apply(form); if (isRegionBlocked(form)) clearMsgs(form); }, 400);
    setTimeout(function () { apply(form); if (isRegionBlocked(form)) clearMsgs(form); }, 1200);
    setTimeout(function () { apply(form); if (isRegionBlocked(form)) clearMsgs(form); }, 3000);
  }

  function scanForms(root) {
    (root || document).querySelectorAll('form[action*="/cart/add"]').forEach(initForm);
  }

  /* ---------------------------------------------------------------------
     Late-injected forms: quick-view modal, sticky add-to-cart bar, any section
     re-render. V2 only ever saw forms present at DOMContentLoaded.
     --------------------------------------------------------------------- */
  function watchForNewForms() {
    if (typeof MutationObserver !== 'function') return;
    var pending = false;
    var obs = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () { pending = false; scanForms(document); registerPhIds(document); }, 50);
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------
     LAST LINE OF DEFENCE: network-level refusal.
     Any /cart/add carrying a known placeholder variant ID is refused here,
     whatever code path produced it - this script's own forms, the quick-add
     modal, a sticky bar, a third-party app, or a script we have never seen.
     Fails OPEN on any internal error: a bug in this block must never be able to
     stop a legitimate add to cart.
     --------------------------------------------------------------------- */
  function idsFromBody(body) {
    var out = [];
    try {
      if (!body) return out;
      if (typeof body === 'string') {
        var re = /(?:^|[&?"'{,\s])(?:id|"id")\s*[:=]\s*"?(\d{6,})"?/g, m;
        while ((m = re.exec(body)) !== null) out.push(m[1]);
        return out;
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var v = body.get('id'); if (v) out.push(String(v));
        if (body.getAll) {
          body.getAll('items[][id]').forEach(function (x) { out.push(String(x)); });
        }
        return out;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        var u = body.get('id'); if (u) out.push(String(u));
        return out;
      }
      if (typeof body === 'object') {
        if (body.id) out.push(String(body.id));
        if (body.items && body.items.length) {
          for (var i = 0; i < body.items.length; i++) {
            if (body.items[i] && body.items[i].id) out.push(String(body.items[i].id));
          }
        }
      }
    } catch (e) { return []; }
    return out;
  }

  function bodyHasPh(body) {
    var ids = idsFromBody(body);
    for (var i = 0; i < ids.length; i++) { if (isPhId(ids[i])) return true; }
    return false;
  }

  function announceBlock() {
    try {
      document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
        if (!isRegionBlocked(f) && isLocked(f)) apply(f);
      });
    } catch (e) {}
  }

  function installNetworkGuard() {
    // fetch
    try {
      if (typeof window.fetch === 'function' && !window.fetch.__bsWrapped) {
        var origFetch = window.fetch;
        var wrapped = function (input, init) {
          try {
            var url = typeof input === 'string' ? input : (input && input.url) || '';
            if (/\/cart\/add/.test(url) && PH_ID_COUNT > 0) {
              var body = (init && init.body) || (input && input.body) || null;
              if (bodyHasPh(body)) {
                announceBlock();
                var payload = JSON.stringify({
                  status: 422,
                  message: 'Cannot add to cart',
                  description: 'Please select your vehicle before adding this item to your cart.'
                });
                if (typeof Response === 'function') {
                  return Promise.resolve(new Response(payload, {
                    status: 422,
                    headers: { 'Content-Type': 'application/json' }
                  }));
                }
                return Promise.reject(new Error('Beat-Sonic: vehicle not selected'));
              }
            }
          } catch (e) { /* fail open */ }
          return origFetch.apply(this, arguments);
        };
        wrapped.__bsWrapped = true;
        window.fetch = wrapped;
      }
    } catch (e) {}

    // XMLHttpRequest. Rather than faking a response on a real XHR object, we
    // strip the body so Shopify itself returns its own 422 and the theme shows a
    // real Shopify error instead of hanging.
    try {
      var XHR = window.XMLHttpRequest;
      if (XHR && XHR.prototype && !XHR.prototype.__bsWrapped) {
        var origOpen = XHR.prototype.open;
        var origSend = XHR.prototype.send;
        XHR.prototype.open = function (method, url) {
          try { this.__bsUrl = url || ''; } catch (e) {}
          return origOpen.apply(this, arguments);
        };
        XHR.prototype.send = function (body) {
          try {
            if (this.__bsUrl && /\/cart\/add/.test(this.__bsUrl) && PH_ID_COUNT > 0 && bodyHasPh(body)) {
              announceBlock();
              if (typeof FormData !== 'undefined') {
                return origSend.call(this, new FormData());
              }
              return origSend.call(this, '');
            }
          } catch (e) { /* fail open */ }
          return origSend.apply(this, arguments);
        };
        XHR.prototype.__bsWrapped = true;
      }
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------
     JSON-LD offerCount safeguard (retained from V2).
     --------------------------------------------------------------------- */
  function fixOfferCount() {
    try {
      document.querySelectorAll('script[type="application/ld+json"]').forEach(function (node) {
        var data; try { data = JSON.parse(node.textContent); } catch (e) { return; }
        var graph = data['@graph'] || [data];
        var changed = false;
        graph.forEach(function (item) {
          if (!item || item['@type'] !== 'Product' || !item.offers) return;
          var count = parseInt(item.offers.offerCount, 10);
          if (!isNaN(count) && count > 0) return;
          var real = 0;
          document.querySelectorAll('form[action*="/cart/add"] select[name="id"] option').forEach(function (o) {
            var t = stripPrice(o.textContent);
            if (!isPh(t) && norm(t).indexOf('DO NOT CONFIGURE') === -1) real++;
          });
          if (real > 0) { item.offers.offerCount = real; } else { delete item.offers; }
          changed = true;
        });
        if (changed) { node.textContent = JSON.stringify(data); }
      });
    } catch (e) {}
  }

  function injectCss() {
    if (document.getElementById('bs-force-select-css')) return;
    var st = document.createElement('style');
    st.id = 'bs-force-select-css';
    st.textContent = '.bs-atc-locked{opacity:.5 !important;cursor:not-allowed !important;}' +
      '.bs-vehicle-lock{pointer-events:none !important;opacity:.45 !important;}' +
      '.bs-select-msg{color:var(--price_color, #e32e00);font-weight:600;margin:.5em 0;}' +
      '[data-bs-blocked="true"] ~ .bs-select-msg{display:none !important;}';
    (document.head || document.documentElement).appendChild(st);
  }

  function boot() {
    injectCss();
    registerPhIds(document);
    installNetworkGuard();
    scanForms(document);
    watchForNewForms();
    fixOfferCount();
    // Browser back / bfcache restore hands back a page whose form controls were
    // restored by the browser, not re-rendered by the theme. Re-assert.
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) {
        document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
          if (!isRegionBlocked(f)) apply(f);
        });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();